import {
  useTheme,
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  IconButton,
} from '@mui/material';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ProjectAddElementButton } from '../../exo/project/ProjectAddElementButton';
import { NewMetaVariableForm } from './NewMetaVariableForm';
import { updateOrCreate } from '../../special/updateOrCreate';
import EditIcon from '@mui/icons-material/Edit';
import { set } from 'lodash-es';

const TemplateVariables = ({
  variables = [],
  onClick,
  className = '',
  metaData,
  onMetaDataChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [variableMode, setVariableMode] = useState('template');

  function groupByPrefix(data) {
    // 1️⃣ Group by prefix (split only by first space)
    const groups = data.reduce((acc, item) => {
      const [prefix, ...rest] = item.name.split(' ');
      const cleanName = rest.join(' ').trim();

      const newItem = { ...item, name: cleanName || item.name }; // fallback if no space

      if (!acc[prefix]) acc[prefix] = [];
      acc[prefix].push(newItem);

      return acc;
    }, {});

    // 2️⃣ Move small groups (<= 2 items) to "Others"
    const others = [];
    for (const [prefix, items] of Object.entries(groups)) {
      if (items.length <= 2) {
        others.push(...items);
        delete groups[prefix];
      }
    }
    if (others.length) groups['Others'] = others;

    // 3️⃣ Sort items alphabetically within each group
    for (const key in groups) {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    }

    // 4️⃣ Sort group keys alphabetically, with "Others" last
    const sortedGroups = Object.keys(groups)
      .sort((a, b) => {
        if (a === 'Others') return 1;
        if (b === 'Others') return -1;
        return a.localeCompare(b);
      })
      .reduce((acc, key) => {
        acc[key] = groups[key];
        return acc;
      }, {});

    return sortedGroups;
  }

  // accordeon elements
  const [expanded, setExpanded] = useState(null);

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };
  const groupedVariables = groupByPrefix(variables);
  const groupKeys = Object.keys(groupedVariables);

  // metafields allows a custopm set of variables to be used in a template
  const { metaFields } = metaData || {};
  function handleMetaDataChange(newOrUpdatedVar) {
    const newMetaFields = updateOrCreate(
      metaFields || [],
      newOrUpdatedVar,
      false,
      'key'
    );

    onMetaDataChange({ ...metaData, metaFields: newMetaFields });
  }

  function handleDeleteMetaVar(varToDelete) {
    const newMetaFields = (metaFields || []).filter(
      (item) => item.key !== varToDelete.key
    );

    onMetaDataChange({ ...metaData, metaFields: newMetaFields });
  }

  return (
    <Box
      className={'h-full w-full py-4 overflow-y-auto rounded-lg ' + className}
      sx={{ backgroundColor: colors.card }}
    >
      <Box className="flex flex-col justify-between items-left px-4">
        <Typography fontSize={16} fontWeight={600} className="opacity-50  pb-2">
          {t('Template Variables')}
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={variableMode}
          exclusive
          onChange={(e) => setVariableMode(e.target.value)}
          aria-label="Variable Mode"
        >
          <ToggleButton value="template">{t('Template')}</ToggleButton>
          <ToggleButton value="meta">{t('custom')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box className="flex flex-col">
        {variableMode === 'template' &&
          groupKeys.map((key, index) => (
            <Accordion
              key={key}
              expanded={expanded === index}
              onChange={handleChange(index)}
              sx={{
                boxShadow: 'none',
              }}
            >
              <AccordionSummary>
                <Typography fontWeight={500} className=" opacity-60">
                  {key}
                </Typography>
              </AccordionSummary>

              <MuiAccordionDetails>
                <VarList varsData={groupedVariables[key]} onClick={onClick} />
              </MuiAccordionDetails>
            </Accordion>
          ))}
        {variableMode === 'meta' && (
          <MetaVarList
            varsData={metaFields}
            onChange={handleMetaDataChange}
            onClick={onClick}
            onDelete={handleDeleteMetaVar}
          />
        )}
      </Box>
    </Box>
  );
};

const VarList = ({ varsData, onClick }) => {
  return (
    <Box className="overflow-auto flex flex-col items-start gap-1">
      {varsData.map((element, index) => (
        <Var onClick={onClick} key={'var' + index} varData={element} />
      ))}
    </Box>
  );
};

const MetaVarList = ({ varsData, onChange, onClick, onDelete }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [varToEdit, setVarToEdit] = useState(null);

  return (
    <Box className="overflow-auto flex flex-col h-full gap-1 w-full pt-2 px-4 justify-start">
      <Collapse in={!open}>
        <ProjectAddElementButton
          label={t('Add new variable')}
          onClick={() => setOpen(true)}
          size="small"
        />
      </Collapse>
      <Collapse in={open}>
        <NewMetaVariableForm
          startData={varToEdit}
          onClose={() => {
            setVarToEdit(null);
            setOpen(false);
          }}
          onDelete={onDelete}
          onChange={onChange}
        />
      </Collapse>

      {varsData &&
        varsData.map((element, index) => (
          <Var
            key={element.key}
            varData={element}
            onEdit={(d) => {
              setOpen(true);
              setVarToEdit(d);
            }}
            onClick={onClick}
          />
        ))}
    </Box>
  );
};

const Var = ({ varData, onClick, onEdit }) => {
  return (
    <Box
      className="flex w-full overflow-hidden items-center gap-1"
      sx={{
        '.button-edit': {
          opacity: 0,
          transform: 'translateX(30px)',
          transition: '0.3s ease',
        },
        ':hover .button-edit': {
          opacity: 1,
          transform: 'translateX(0px)',
        },
      }}
    >
      <Button
        onClick={() => onClick(varData.value)}
        className="rounded-lg p-1 w-full"
        sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
        variant="outlined"
        color="secondary"
      >
        {varData.name}
      </Button>
      {onEdit && (
        <IconButton
          onClick={() => onEdit(varData)}
          size="small"
          className="button-edit"
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );
};
export default TemplateVariables;
